import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button, Card, CardBody, CardFooter,
  Input, Label,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useCreateAffiliate } from '@/api/affiliate';
import {
  affiliateNewSchema, TIER_OPTIONS, type AffiliateNewFormValues,
} from './affiliate-new-schema';
import { CustomerSearchSelect } from '@/pages/contracts/new/customer-search-select';

/**
 * `/affiliates/new` — full-page create form for a new affiliate. Mirrors the
 * legacy New component's fields and validation: referrer info section, commission
 * settings section (tier / fallback commission / payout threshold / referral code).
 *
 * URL query params pre-fill fields (customer_id, referrer_name, referrer_email,
 * referrer_phone, referrer_company) — used when launching from a customer contact.
 *
 * On success navigates to `/affiliates/:id`.
 */
export default function AffiliateNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitError, setSubmitError] = useState('');

  const createAffiliate = useCreateAffiliate();

  const {
    register, control, setValue, handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AffiliateNewFormValues>({
    resolver: yupResolver(affiliateNewSchema) as never,
    defaultValues: {
      referrer_name: '',
      referrer_email: '',
      referrer_phone: '',
      referrer_company: '',
      customer_id: null,
      referral_code: '',
      tier: 'standard',
      commission_percent: undefined,
      payout_threshold: 500000,
    },
  });

  /* Pre-fill from URL search params (launched from customer contact context) */
  useEffect(() => {
    const customerId = Number(searchParams.get('customer_id'));
    if (customerId) setValue('customer_id', customerId);

    const prefillFields = [
      'referrer_name',
      'referrer_email',
      'referrer_phone',
      'referrer_company',
    ] as const;
    for (const field of prefillFields) {
      const value = searchParams.get(field);
      if (value) setValue(field, value);
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: AffiliateNewFormValues) => {
    setSubmitError('');
    try {
      const res = await createAffiliate.mutateAsync({
        referrer_name: data.referrer_name,
        referrer_email: data.referrer_email,
        referrer_phone: data.referrer_phone || undefined,
        referrer_company: data.referrer_company || undefined,
        customer_id: data.customer_id ?? undefined,
        referral_code: data.referral_code || undefined,
        tier: data.tier as 'standard' | 'silver' | 'gold' | 'platinum',
        commission_percent: data.commission_percent,
        payout_threshold: data.payout_threshold,
      });
      const newId = res?.result?.affiliate_id;
      navigate(newId ? `/affiliates/${newId}` : '/affiliates');
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to create affiliate. Please try again.',
      );
    }
  };

  const isPending = isSubmitting || createAffiliate.isPending;

  return (
    <Container size="lg">
      <PageHeader
        title="New Affiliate"
        description="Register a new affiliate referrer in the program."
        actions={
          <Button
            variant="ghost"
            startIcon={<ArrowLeft size={15} />}
            onClick={() => navigate('/affiliates')}
          >
            Back to affiliates
          </Button>
        }
      />

      <Card>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>

            {/* Referrer information */}
            <div>
              <Label style={{ display: 'block', marginBottom: 'var(--vn-space-3)', color: 'var(--vn-fg-muted)' }}>
                Referrer information
              </Label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-4)' }}>
                <Field label="Full Name" required error={errors.referrer_name?.message}>
                  {(fp) => (
                    <Input
                      {...fp}
                      {...register('referrer_name')}
                      placeholder="Full name"
                      invalid={!!errors.referrer_name}
                    />
                  )}
                </Field>

                <Field label="Email" required error={errors.referrer_email?.message}>
                  {(fp) => (
                    <Input
                      {...fp}
                      {...register('referrer_email')}
                      type="email"
                      placeholder="email@example.com"
                      invalid={!!errors.referrer_email}
                    />
                  )}
                </Field>

                <Field label="Phone" error={errors.referrer_phone?.message}>
                  {(fp) => (
                    <Input
                      {...fp}
                      {...register('referrer_phone')}
                      placeholder="Phone number"
                      invalid={!!errors.referrer_phone}
                    />
                  )}
                </Field>

                <Field label="Company" error={errors.referrer_company?.message}>
                  {(fp) => (
                    <Input
                      {...fp}
                      {...register('referrer_company')}
                      placeholder="Company name (optional)"
                      invalid={!!errors.referrer_company}
                    />
                  )}
                </Field>

                <Controller
                  name="customer_id"
                  control={control}
                  render={({ field }) => (
                    <CustomerSearchSelect
                      label="Link to Customer"
                      value={field.value ?? 0}
                      onChange={(id) => field.onChange(id > 0 ? id : null)}
                      error={errors.customer_id?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Commission settings */}
            <div>
              <Label style={{ display: 'block', marginBottom: 'var(--vn-space-3)', color: 'var(--vn-fg-muted)' }}>
                Commission settings
              </Label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-4)' }}>
                <Field label="Tier" error={errors.tier?.message}>
                  {(fp) => (
                    <Controller
                      name="tier"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger {...fp} invalid={!!errors.tier}>
                            <SelectValue placeholder="Select tier" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIER_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  )}
                </Field>

                <Field
                  label="Fallback Commission %"
                  description="Used only when no commission policy matches. Leave blank to let the server decide."
                  error={errors.commission_percent?.message}
                >
                  {(fp) => (
                    <Input
                      {...fp}
                      {...register('commission_percent', { valueAsNumber: true })}
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      placeholder="Optional (e.g. 5)"
                      invalid={!!errors.commission_percent}
                    />
                  )}
                </Field>

                <Field label="Payout Threshold (₫)" error={errors.payout_threshold?.message}>
                  {(fp) => (
                    <Input
                      {...fp}
                      {...register('payout_threshold', { valueAsNumber: true })}
                      type="number"
                      min={0}
                      placeholder="500000"
                      invalid={!!errors.payout_threshold}
                    />
                  )}
                </Field>

                <Field
                  label="Referral Code"
                  description="Auto-generated if left blank."
                  error={errors.referral_code?.message}
                >
                  {(fp) => (
                    <Input
                      {...fp}
                      {...register('referral_code')}
                      placeholder="e.g. MYCODE2024 (optional)"
                      invalid={!!errors.referral_code}
                    />
                  )}
                </Field>
              </div>
            </div>

            {submitError && (
              <Alert tone="danger" title={submitError} />
            )}
          </CardBody>

          <CardFooter>
            <Button
              variant="ghost"
              onClick={() => navigate('/affiliates')}
              disabled={isPending}
              type="button"
            >
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              Create affiliate
            </Button>
          </CardFooter>
        </form>
      </Card>
    </Container>
  );
}
